'use client'
import { Grid, IconButton } from '@achieve/ascend'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { useRef, useState } from 'react'
import { DotIndicators } from 'components/DotIndicators'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import classNames from 'classnames'
import { SITE_ENVIRONMENTS } from 'constants/websites'

const CAROUSEL_DIRECTIONS = {
  PREVIOUS: 'PREVIOUS',
  NEXT: 'NEXT',
}

const getItemDetails = ({ itemIndex, activeIndex, listLength }) => {
  return {
    // isActive=true when the activeIndex from state matches the item itemIndex
    isActive: itemIndex === activeIndex,

    // When the current activeIndex is the first item in the list, isPrevious=true if it
    // is the last item in the list. Otherwise, isPrevious=true if this item's itemIndex is
    // one less than the activeIndex.
    isPrevious: activeIndex === 0 ? itemIndex === listLength - 1 : itemIndex === activeIndex - 1,

    // When the current activeIndex is the last item in the list, isNext=true if it is the
    // first item in the list. Otherwise, isNext=true if this item's itemIndex is one more
    // than the activeIndex.
    isNext: activeIndex === listLength - 1 ? itemIndex === 0 : itemIndex === activeIndex + 1,
  }
}

const CarouselButton = ({ direction = '', onClick }) => {
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  if (!direction) return

  const isPrevious = direction === CAROUSEL_DIRECTIONS.PREVIOUS

  return (
    <div className={'[&_button]:hover:bg-blue-100!'}>
      <IconButton
        onClick={() => onClick()}
        aria-label={`Display ${isPrevious ? 'Previous' : 'Next'} item in carousel`}
        className={classNames(
          // TODO move to tailwind config for FDR
          siteEnv === SITE_ENVIRONMENTS.FDR ? '[&_svg]:text-[#154199]' : '[&_svg]:text-[#30f]',
          'h-[50px] w-[50px] bg-white shadow-[0_2px_9px_-2px_#E7ECF3,0_2px_3px_-2px_#c0cbd8] rounded-[50%]'
        )}
      >
        {isPrevious ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>
    </div>
  )
}

const CarouselItemMessage = ({ children, activeIndex, itemIndex, listLength }) => {
  const { isActive, isPrevious, isNext } = getItemDetails({
    activeIndex,
    itemIndex,
    listLength,
  })

  return (
    <Grid
      data-active={isActive}
      data-next={isNext}
      data-previous={isPrevious}
      className={
        'opacity-0 z-0 h-0 overflow-y-hidden top-0 data-[active="true"]:opacity-100 data-[active="true"]:z-1 data-[active="true"]:h-auto data-[active="true"]:mt-14'
      }
      container
      direction="column"
      alignItems="center"
    >
      {children}
    </Grid>
  )
}

const CarouselItem = ({
  children,
  activeIndex,
  itemIndex,
  listLength,
  onPreviousClick,
  onNextClick,
  id,
}) => {
  const { isActive, isPrevious, isNext } = getItemDetails({
    activeIndex,
    itemIndex,
    listLength,
  })

  const handleClick = (e) => {
    if (isActive) return
    if (isPrevious) return onPreviousClick(e)
    if (isNext) return onNextClick(e)
  }
  return (
    <div
      id={id}
      data-active={isActive}
      data-next={isNext}
      data-previous={isPrevious}
      data-animation-direction={isPrevious ? 'Left' : 'Right'}
      className={classNames(
        //data-variant=standard
        'group-data-[variant="standard"]:absolute group-data-[variant="standard"]:-translate-x-2/4 group-data-[variant="standard"]:opacity-0 group-data-[variant="standard"]:[transition:left_250ms_cubic-bezier(0.4,0,0.2,1),transform_250ms_cubic-bezier(0.4,0,0.2,1),opacity_250ms_cubic-bezier(0.4,0,0.2,1),filter_250ms_cubic-bezier(0.4,0,0.2,1)] group-data-[variant="standard"]:brightness-[0.7] group-data-[variant="standard"]:z-0 group-data-[variant="standard"]:left-2/4 lg:max-w-[initial]',
        //data-variant=standard data-active=true
        'data-[active="true"]:group-data-[variant="standard"]:z-2 data-[active="true"]:group-data-[variant="standard"]:opacity-100 data-[active="true"]:group-data-[variant="standard"]:brightness-100 data-[active="true"]:group-data-[variant="standard"]:shadow-[0_1px_1px_0_rgb(16_24_40/3%),0_0.5px_3px_0_rgb(16_24_40/9%)]',
        //data-variant=standard data-previous=true
        'data-[previous="true"]:group-data-[variant="standard"]:z-1 data-[previous="true"]:group-data-[variant="standard"]:-translate-x-2/4 data-[previous="true"]:group-data-[variant="standard"]:opacity-100 data-[previous="true"]:group-data-[variant="standard"]:scale-[0.85] data-[previous="true"]:group-data-[variant="standard"]:left-1/4',
        //data-variant=standard data-next=true
        'data-[next="true"]:group-data-[variant="standard"]:z-1 data-[next="true"]:group-data-[variant="standard"]:-translate-x-2/4 data-[next="true"]:group-data-[variant="standard"]:opacity-100 data-[next="true"]:group-data-[variant="standard"]:scale-[0.85] data-[next="true"]:group-data-[variant="standard"]:left-3/4',
        //data-variant=cards
        'group-data-[variant="cards"]:absolute group-data-[variant="cards"]:-translate-x-2/4 group-data-[variant="cards"]:opacity-0 group-data-[variant="cards"]:brightness-[0.7] group-data-[variant="cards"]:z-0 group-data-[variant="cards"]:left-2/4 group-data-[variant="cards"]:[transition:left_250ms_cubic-bezier(0.4,0,0.2,1),transform_250ms_cubic-bezier(0.4,0,0.2,1),opacity_250ms_cubic-bezier(0.4,0,0.2,1),filter_250ms_cubic-bezier(0.4,0,0.2,1)] lg:group-data-[variant="cards"]:max-w-[initial]',
        //data-variant=cards data-active=true
        'data-[active="true"]:group-data-[variant="cards"]:z-2 data-[active="true"]:group-data-[variant="cards"]:opacity-100 data-[active="true"]:group-data-[variant="cards"]:brightness-100 data-[active="true"]:group-data-[variant="cards"]:shadow-[0_1px_1px_0_rgb(16_24_40/3%),0_0.5px_3px_0_rgb(16_24_40/9%)]',
        //data-variant=cards data-previous=true
        'data-[previous="true"]:group-data-[variant="cards"]:z-1 data-[previous="true"]:group-data-[variant="cards"]:-translate-x-2/4 data-[previous="true"]:group-data-[variant="cards"]:scale-[0.85] data-[previous="true"]:group-data-[variant="cards"]:left-1/4',
        //data-variant=cards data-next=true
        'data-[next="true"]:group-data-[variant="cards"]:z-1 data-[next="true"]:group-data-[variant="cards"]:-translate-x-2/4 data-[next="true"]:group-data-[variant="cards"]:scale-[0.85] data-[next="true"]:group-data-[variant="cards"]:left-3/4'
      )}
      onClick={handleClick}
    >
      {children}
    </div>
  )
}

const CarouselV2 = ({
  gridItems,
  ItemComponentCarousel,
  ItemComponentMessage,
  withMessages = false,
  variant = 'standard',
  trackingNameCarouselControls = 'Carousel Nav',
  event,
  config,
  variationDot = null,
}) => {
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const showTitle = config?.showItemTitle ?? true
  // Retrieving AnalyticsContext for dispatching events
  const { fireAndForget } = useTriggerEvents()

  const getInitialIndex = () => {
    if (typeof window === 'undefined') return { index: 0, shouldAutoplay: false }

    const hash = window.location.hash.slice(1) // Remove the '#' prefix
    if (!hash) return { index: 0, shouldAutoplay: false }

    const matchedIndex = gridItems.findIndex((item) => item?.config?.id === hash)
    return matchedIndex !== -1
      ? { index: matchedIndex, shouldAutoplay: true }
      : { index: 0, shouldAutoplay: false }
  }

  const initialData = getInitialIndex()
  const [activeIndex, setActiveIndex] = useState(initialData.index)
  const [shouldAutoplay, setShouldAutoplay] = useState(initialData.shouldAutoplay)
  const minSwipeDistance = 50
  let touchStart = useRef()
  let touchEnd = useRef()

  const dispatchCarouselEvent = ({ localEvent, direction = 'Right', index }) => {
    setShouldAutoplay(false) // Disable autoplay when user manually navigates
    return fireAndForget({
      ...handleTrackAndReactEvent(
        localEvent,
        {
          list_name: trackingNameCarouselControls,
          click_id: `${direction} Carousel`,
          click_text: `${direction} Carousel`,
          click_type: 'UI Click',
          click_position: index,
          nav_link_section: 'Testimonials',
          track_event: 'ui_click',
          event_action: 'ui_click',
          event_type: 'ui_click',
          ...event,
        },
        () => setActiveIndex(index)
      ),
    })
  }

  const onPreviousClick = (event) => {
    // When the current activeIndex is the first item in the list, loop to the end and set the
    // last item in the list as the activeIndex. Otherwise efault to the previous index
    const nextIndex = activeIndex === 0 ? gridItems.length - 1 : activeIndex - 1
    dispatchCarouselEvent({ event, direction: 'Left', index: nextIndex })
  }

  const onNextClick = (event) => {
    // When the current activeIndex is the last item in the list, loop to the beginning and set
    // the first item in the list as the activeIndex. Otherwise set the next item as the activeIndex
    const nextIndex = activeIndex === gridItems.length - 1 ? 0 : activeIndex + 1
    dispatchCarouselEvent({ event, index: nextIndex })
  }

  return (
    <>
      {/* Carousel and Controls Wrapper */}
      <Grid
        container
        direction="column"
        alignItems="center"
        onTouchStart={(e) => {
          touchEnd = null
          touchStart = e.targetTouches[0].clientX
        }}
        onTouchMove={(e) => {
          touchEnd = e.targetTouches[0].clientX
        }}
        onTouchEnd={(e) => {
          if (!touchStart || !touchEnd) return
          const distance = touchStart - touchEnd
          const isLeftSwipe = distance > minSwipeDistance
          const isRightSwipe = distance < -minSwipeDistance

          if (!isLeftSwipe && !isRightSwipe) return
          if (isLeftSwipe) return onNextClick(e)
          onPreviousClick(e)
        }}
        className={
          'group data-[variant="standard"]:relative data-[variant="standard"]:w-full data-[variant="cards"]:relative data-[variant="cards"]:w-full'
        }
        style={{
          height: variant === 'standard' ? (isMobile ? '208px' : '364px') : '440px',
        }}
        data-variant={variant}
      >
        {/* Controls Component */}
        {(variant === 'standard' || !isMobile) && (
          <div
            className={classNames(
              //data-variant=standard
              'group-data-[variant="standard"]:opacity-0 group-data-[variant="standard"]:absolute group-data-[variant="standard"]:flex group-data-[variant="standard"]:w-full group-data-[variant="standard"]:justify-between group-data-[variant="standard"]:-translate-y-2/4 z-1 group-data-[variant="standard"]:px-4 group-data-[variant="standard"]:py-0 group-data-[variant="standard"]:top-2/4 md:group-data-[variant="standard"]:opacity-100',
              //data-variant=cards
              'group-data-[variant="cards"]:opacity-0 group-data-[variant="cards"]:absolute group-data-[variant="cards"]:flex group-data-[variant="cards"]:w-full group-data-[variant="cards"]:justify-between group-data-[variant="cards"]:-translate-y-2/4 group-data-[variant="cards"]:z-3 group-data-[variant="cards"]:px-4 group-data-[variant="cards"]:py-0 group-data-[variant="cards"]:top-2/4 hover:[&_button]:group-data-[variant="cards"]:bg-white md:group-data-[variant="cards"]:opacity-100'
            )}
          >
            <CarouselButton direction={CAROUSEL_DIRECTIONS.PREVIOUS} onClick={onPreviousClick} />
            <CarouselButton direction={CAROUSEL_DIRECTIONS.NEXT} onClick={onNextClick} />
          </div>
        )}

        <div
          className={
            'group-data-[variant="standard"]:relative group-data-[variant="standard"]:w-full group-data-[variant="cards"]:max-w-[650px] lg:group-data-[variant="standard"]:max-w-[initial]'
          }
          data-testid="carousel-items"
        >
          {/* Carousel Items */}
          {gridItems.map((item, index) => (
            <CarouselItem
              key={`carousel-item-${index}`}
              id={item?.config?.id || `carousel-item-${index}`}
              activeIndex={activeIndex}
              itemIndex={index}
              listLength={gridItems.length}
              onPreviousClick={onPreviousClick}
              onNextClick={onNextClick}
            >
              <ItemComponentCarousel
                key={`carousel-item-${index}`}
                item={item}
                index={index}
                activeIndex={activeIndex}
                showTitle={showTitle}
              />
            </CarouselItem>
          ))}
        </div>
      </Grid>

      {/* Carousel Item Message */}
      {withMessages &&
        gridItems.map((item, index) => (
          <CarouselItemMessage
            key={`carousel-item-message-${index}`}
            activeIndex={activeIndex}
            itemIndex={index}
            listLength={gridItems.length}
            shouldAutoplay={shouldAutoplay && index === activeIndex}
          >
            <ItemComponentMessage
              item={item}
              shouldAutoplay={shouldAutoplay && index === activeIndex}
            />
          </CarouselItemMessage>
        ))}

      <DotIndicators
        classNameIndicators={'mt-8'}
        size={gridItems.length}
        activeIndex={activeIndex}
        variant={variationDot ? variationDot : 'onWhite'}
      />
    </>
  )
}

export { CarouselV2 }

export default CarouselV2
